/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ImageIcon from '@atlaskit/icon/core/image';
import Link from '@atlaskit/link';
import { CloseButton, type OnCloseHandler } from '@atlaskit/modal-dialog';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Tag from '@atlaskit/tag';
import TagGroup from '@atlaskit/tag-group';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';

import { type ObjectData, type ObjectTileData } from '../types';

import CodeCopyToClipboard from './code-copy-to-clipboard';
import CodeObjectCopyToClipboard from './code-object-copy-to-clipboard';

export type ObjectDetailsProps = {
	object?: ObjectData | ObjectTileData;
	testId?: string;
	isModal?: boolean;
	onClose?: OnCloseHandler;
	isObjectTile?: boolean;
	isBold?: boolean;
	onBoldToggle?: (isBold: boolean) => void;
	sizes?: Array<{ label: string; element: React.ReactNode; code: string }>;
};

const styles = cssMap({
	boxPanel: {
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		width: '336px',
	},
	boxPadding: {
		paddingInline: token('space.300'),
		paddingBlock: token('space.300'),
	},
	header: {
		paddingInline: token('space.600'),
		paddingBlock: token('space.300'),
	},
	closeButton: {
		position: 'absolute',
		insetInlineEnd: token('space.200'),
		insetBlockStart: token('space.200'),
	},
	sharedBoxBorder: {
		borderBlockEndColor: token('color.border'),
		borderBlockEndStyle: 'solid',
		borderWidth: token('border.width'),
	},
});

/**
 * __Object details__
 *
 * A panel which is populated when an object is selected.
 * Contains information about the object.
 */
export const ObjectDetails: ({
	object,
	testId,
	isModal,
	onClose,
	isObjectTile,
	isBold,
	onBoldToggle,
	sizes,
}: ObjectDetailsProps) => JSX.Element = ({
	object,
	testId,
	isModal,
	onClose,
	isObjectTile,
	isBold,
	onBoldToggle,
	sizes,
}: ObjectDetailsProps) => {
	return object ? (
		<Box xcss={!isModal && styles.boxPanel}>
			<Stack testId={testId} grow="fill">
				<Box
					xcss={cx(styles.sharedBoxBorder, styles.header)}
					backgroundColor="elevation.surface.sunken"
				>
					<Inline alignInline="center">
						{isObjectTile ? (
							<object.component label="" size="medium" />
						) : (
							<object.component label="" />
						)}
					</Inline>
					{isModal && onClose && (
						<Box xcss={styles.closeButton}>
							<CloseButton onClick={onClose} />
						</Box>
					)}
				</Box>
				<Box xcss={cx(styles.sharedBoxBorder, styles.boxPadding)}>
					<Stack space="space.150">
						<Inline alignBlock="center" space="space.050">
							<Heading size="medium">{object.objectName}</Heading>
						</Inline>
						<TagGroup label="keywords" alignment="start">
							{object.keywords.map((keyword) => (
								<Tag key={keyword} text={keyword} isRemovable={false} />
							))}
						</TagGroup>
					</Stack>
				</Box>
				<Box xcss={cx(styles.sharedBoxBorder, styles.boxPadding)}>
					<Stack space="space.150">
						<Heading size="small">React</Heading>
						<CodeCopyToClipboard
							messages={{
								prompt: 'Copy import',
								success: 'Copied!',
								fail: 'Copy failed',
							}}
							value={`import ${object.componentName} from '${object.packageName}';`}
						>
							import {object.componentName} from '{object.packageName}';
						</CodeCopyToClipboard>
						<Link
							href={
								isObjectTile
									? '/components/object/object-tile/examples'
									: '/components/object/examples'
							}
							target="_blank"
						>
							{isObjectTile ? 'Object Tile' : 'Object'} code examples
						</Link>
					</Stack>
				</Box>
				{isObjectTile && onBoldToggle && (
					<Box xcss={cx(styles.sharedBoxBorder, styles.boxPadding)}>
						<Stack space="space.150">
							<Heading size="small">Appearance</Heading>
							<Inline space="space.100" alignBlock="center">
								<Toggle
									id="bold-toggle"
									label="Bold"
									isChecked={isBold || false}
									onChange={() => onBoldToggle(!isBold)}
								/>
								<Text>Bold</Text>
							</Inline>
						</Stack>
					</Box>
				)}
				{sizes && sizes.length > 0 && (
					<Box xcss={cx(styles.sharedBoxBorder, styles.boxPadding)}>
						<Stack space="space.150">
							<Heading size="small">Sizes</Heading>
							<Stack space="space.0">
								{sizes.map((size) => (
									<CodeObjectCopyToClipboard
										key={size.label}
										name={size.label}
										objectElement={size.element}
										value={size.code}
										prompt="Copy usage code"
									/>
								))}
							</Stack>
						</Stack>
					</Box>
				)}
				{/* TODO: Add Figma links once branch is merged */}
				{/* <Box xcss={styles.boxPadding}>
					<Stack space="space.150">
						<Heading size="small">Figma</Heading>
						<Link href="https://www.figma.com/design/GAUMGpQ0veCj4JbcYdKrounds" target="_blank">
							Object library (Atlassians only)
						</Link>
					</Stack>
				</Box> */}
			</Stack>
		</Box>
	) : (
		<Box xcss={cx(styles.boxPanel, styles.boxPadding)} backgroundColor="elevation.surface.sunken">
			<Stack space="space.150" testId={testId} alignInline="center" grow="fill">
				<ImageIcon label="" color="currentColor" />
				<Text color="color.text.subtle" align="center">
					Select {isObjectTile ? 'an object tile' : 'an object'} for details
				</Text>
			</Stack>
		</Box>
	);
};
